"use client";

import { useState } from "react";
import { Input } from "../input";

export interface OpenCageResult {
  formatted: string;
  components?: {
    postcode?: string;
    city?: string;
    town?: string;
    village?: string;
    municipality?: string;
    locality?: string;
    country?: string;
  };
}

interface AddressAutocompleteProps {
  id: string;
  label?: string;
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
  onSelect: (address: {
    formatted: string;
    country: string;
    postalCode?: string;
    city?: string;
  }) => void;
  results: OpenCageResult[];
  error?: string;
  loading?: boolean;
  onBlur?: (value: string) => void;
  cleanFormattedAddress?: (
    formatted: string,
    country?: string,
    postalCode?: string,
    city?: string
  ) => string;
}
const BLUR_TIMEOUT_MS = 150;
export function AddressAutocompleteInput({
  label = "Address",
  id,
  placeholder,
  value,
  onChange,
  onSelect,
  results,
  error,
  onBlur,
  cleanFormattedAddress,
}: AddressAutocompleteProps) {
  const [showResults, setShowResults] = useState(false);

  const preserveHouseNumber = (
    userInput: string,
    baseAddress: string
  ): string => {
    const match = userInput.trim().match(/^[0-9]+[a-zA-Z]?/);
    if (match && !baseAddress.startsWith(match[0])) {
      return `${match[0]} ${baseAddress}`.trim();
    }
    return baseAddress;
  };

  return (
    <div className="relative">
      <Input
        autoComplete="off"
        hasIcon={false}
        id={id}
        label={`${label}`}
        message={error}
        name=""
        onBlur={(event) => {
          const { value: inputValue } = event.target;
          setTimeout(() => setShowResults(false), BLUR_TIMEOUT_MS);
          onBlur?.(inputValue);
        }}
        onChange={(e) => {
          onChange(e.target.value);
          setShowResults(true);
        }}
        placeholder={placeholder}
        status={error ? "error" : null}
        type="text"
        value={value}
      />

      {showResults && results.length > 0 && (
        <ul className="absolute right-0 left-0 z-10 mt-1 max-h-56 overflow-y-auto border border-gray-200 bg-white">
          {results.map((r: OpenCageResult, i: number) => (
            <li
              className="cursor-pointer px-3 py-2 text-gray-900 text-sm hover:bg-gray-100"
              key={`result-${i + 1}`}
              onMouseDown={() => {
                // use onMouseDown so blur on input doesn't hide before click
                const postal = r.components?.postcode || "";
                const cityName =
                  r.components?.city ||
                  r.components?.town ||
                  r.components?.village ||
                  r.components?.municipality ||
                  r.components?.locality ||
                  "";
                let cleaned = cleanFormattedAddress
                  ? cleanFormattedAddress(
                      r.formatted,
                      r.components?.country,
                      postal,
                      cityName
                    )
                  : "";
                cleaned = preserveHouseNumber(value, cleaned);
                onSelect({
                  formatted: cleaned,
                  country: r.components?.country ?? "",
                  postalCode: postal,
                  city: cityName,
                });
                setShowResults(false);
              }}
            >
              {r.formatted}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

AddressAutocompleteInput.displayName = "AddressAutocompleteInput";
